import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusTag, type PlainTableColumn } from '@/components/ui';
import type { UnifiedAuditRow } from '@/api/console';
import { ACTION_LABELS, RESULT_STATUS_CONFIG } from './actionLabels';
import { ActorCell, TargetCell } from './auditCells';
import type { AuditFacetOptions, AuditFilters } from './auditFilterTypes';
import { AuditColumnFilter } from './filters';

/**
 * EAA-1821: the six Nhật ký columns — each one's label, width, sort field, its
 * header control (a funnel onto the same option list the filter bar offers, or
 * a magnifier onto a free-text box) and the renderer for its cell.
 *
 * ONE descriptor per column, not a header file plus a body file. The header and
 * the cell below it are two halves of the same fact — "column 5 is the
 * credential mode, filtered by these options, rendered like this" — and while
 * they lived apart the mode column's header could offer options the cell had no
 * label for. Keeping them in one object makes that shape impossible to write.
 *
 * The `<table>` scaffolding itself is `packages/ui`'s `PlainTable`; this file is
 * the part that is genuinely about auditing. It is a pure derivation: it owns no
 * filter state and issues no request — `filters` comes down, `onFiltersChange`
 * goes back up to the panel, which is the only side that knows how a filter
 * becomes a query param.
 */

export interface UseAuditColumnsInput {
    /**
     * Account source: column 5 is "Chế độ" over `credential_mode`. Admin source:
     * the same slot becomes the target-type category, since admin rows carry no
     * credential mode at all (see `UnifiedAuditRow.credential_mode`).
     */
    showCredentialMode: boolean;
    filters: AuditFilters;
    onFiltersChange: (next: AuditFilters) => void;
    options: AuditFacetOptions;
}

/**
 * EAA-1821: the three sortable columns are keyed by the BACKEND's `ordering`
 * field name, so `PlainTable`'s `onSort(key)` hands back a value that is already
 * an `AuditSortField`. Keying them by a display name would mean a lookup table
 * whose only job is to undo a rename.
 */
const COLUMN_WIDTHS = {
    created_at: '17%',
    actor: '18%',
    action: '17%',
    target: '20%',
    mode: '14%',
    success: '14%',
} as const;

export function useAuditColumns({
    showCredentialMode,
    filters,
    onFiltersChange,
    options,
}: UseAuditColumnsInput): PlainTableColumn<UnifiedAuditRow>[] {
    const { t, i18n } = useTranslation();

    // EAA-1821: the timestamp follows the UI language instead of the hardcoded
    // 'vi-VN' this cell used to carry — an operator who switched the console to
    // English still read Vietnamese-ordered dates. One formatter per language
    // rather than one per cell: `Intl.DateTimeFormat` construction is the
    // expensive half, and a 50-row page built 50 of them per render.
    //
    // DATE AND TIME ARE FORMATTED SEPARATELY, then joined date-first. Asking one
    // formatter for both lets the locale decide the order, and vi-VN puts the
    // clock first ("14:58 10/08/2026") — the design frame, and every other
    // timestamp in this console, reads date-then-time. Only the order is pinned;
    // the separators inside each half stay the locale's own.
    const dateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(i18n.language, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }),
        [i18n.language]
    );
    const timeFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(i18n.language, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }),
        [i18n.language]
    );
    const formatTimestamp = useCallback(
        (iso: string): string => {
            const at = new Date(iso);
            return `${dateFormatter.format(at)} ${timeFormatter.format(at)}`;
        },
        [dateFormatter, timeFormatter]
    );

    const handleDateChange = useCallback(
        (date: AuditFilters['date']) => onFiltersChange({ ...filters, date }),
        [filters, onFiltersChange]
    );
    const handleActorChange = useCallback(
        (actor: string) => onFiltersChange({ ...filters, actor }),
        [filters, onFiltersChange]
    );
    const handleActionChange = useCallback(
        (action: string[]) => onFiltersChange({ ...filters, action }),
        [filters, onFiltersChange]
    );
    const handleTargetChange = useCallback(
        (target: string) => onFiltersChange({ ...filters, target }),
        [filters, onFiltersChange]
    );
    const handleModeChange = useCallback(
        (next: string[]) =>
            onFiltersChange(
                showCredentialMode
                    ? { ...filters, credentialMode: next }
                    : { ...filters, targetType: next }
            ),
        [filters, onFiltersChange, showCredentialMode]
    );
    // EAA-1821: `AuditFilters.success` is a SINGLE value ('' | 'true' | 'false')
    // — "thành công AND thất bại" is just "no filter" — but the header control is
    // the shared multi-select popover. Bridge it here rather than forking the
    // popover: the last box ticked wins, so ticking a second one behaves as
    // switching, and unticking clears the facet.
    const successValue = useMemo(
        () => (filters.success ? [filters.success] : []),
        [filters.success]
    );
    const handleSuccessChange = useCallback(
        (next: string[]) => onFiltersChange({ ...filters, success: next[next.length - 1] ?? '' }),
        [filters, onFiltersChange]
    );

    // EAA-1821: the "Chế độ" column reads its display labels off the SAME
    // `options` the popover offers, so a mode can never render as a raw code in
    // the cell while showing a friendly label one popover away.
    const modeLabels = useMemo(() => {
        const source = showCredentialMode ? options.credentialMode : options.targetType;
        return new Map(source.map((option) => [option.value, option.label]));
    }, [showCredentialMode, options.credentialMode, options.targetType]);

    const modeValueOf = useCallback(
        (row: UnifiedAuditRow): string => {
            if (showCredentialMode) return row.credential_mode ?? '';
            const separatorIndex = row.target.indexOf(':');
            return separatorIndex >= 0 ? row.target.slice(0, separatorIndex) : '';
        },
        [showCredentialMode]
    );

    return useMemo(() => {
        const clearLabel = t('console.audit.columnFilter.clear', {
            defaultValue: 'Bỏ chọn tất cả',
        });
        const clearSearchLabel = t('console.audit.columnFilter.clearSearch', {
            defaultValue: 'Xoá tìm kiếm',
        });
        const modeLabel = showCredentialMode
            ? t('console.audit.columns.credentialMode', { defaultValue: 'Chế độ' })
            : t('console.audit.columns.targetCategory', { defaultValue: 'Loại đối tượng' });
        const modeActive = showCredentialMode
            ? filters.credentialMode.length > 0
            : filters.targetType.length > 0;

        return [
            {
                key: 'created_at',
                label: t('console.audit.columns.time', { defaultValue: 'Thời điểm' }),
                width: COLUMN_WIDTHS.created_at,
                sortable: true,
                control: (
                    <AuditColumnFilter
                        variant="date"
                        active={filters.date.preset !== 'all'}
                        value={filters.date}
                        onChange={handleDateChange}
                        title={t('console.audit.columnFilter.time', {
                            defaultValue: 'Lọc theo thời điểm',
                        })}
                        clearLabel={clearLabel}
                        // EAA-1821: the funnel gets the SAME translated preset copy
                        // as the filter bar's date chip. It used to get none, so the
                        // header menu stayed Vietnamese while the bar spoke English.
                        labels={options.dateLabels}
                    />
                ),
                cellClassName: 'portal-audit-time',
                render: (row) => formatTimestamp(row.created_at),
            },
            {
                key: 'actor',
                label: t('console.audit.columns.actor', { defaultValue: 'Người thao tác' }),
                width: COLUMN_WIDTHS.actor,
                control: (
                    <AuditColumnFilter
                        variant="search"
                        active={Boolean(filters.actor.trim())}
                        value={filters.actor}
                        onChange={handleActorChange}
                        title={t('console.audit.columnFilter.actor', {
                            defaultValue: 'Tìm theo người thao tác',
                        })}
                        placeholder={t('console.audit.columnFilter.actorPlaceholder', {
                            defaultValue: 'Tên đăng nhập…',
                        })}
                        clearLabel={clearSearchLabel}
                    />
                ),
                render: (row) => <ActorCell row={row} />,
            },
            {
                key: 'action',
                label: t('console.audit.columns.action', { defaultValue: 'Hành động' }),
                width: COLUMN_WIDTHS.action,
                sortable: true,
                control: (
                    <AuditColumnFilter
                        variant="filter"
                        active={filters.action.length > 0}
                        options={options.action}
                        value={filters.action}
                        onChange={handleActionChange}
                        title={t('console.audit.columnFilter.action', {
                            defaultValue: 'Lọc theo hành động',
                        })}
                        clearLabel={clearLabel}
                    />
                ),
                render: (row) => ACTION_LABELS[row.action] ?? row.action,
            },
            {
                key: 'target',
                label: t('console.audit.columns.target', { defaultValue: 'Đối tượng' }),
                width: COLUMN_WIDTHS.target,
                control: (
                    <AuditColumnFilter
                        variant="search"
                        active={Boolean(filters.target.trim())}
                        value={filters.target}
                        onChange={handleTargetChange}
                        title={t('console.audit.columnFilter.target', {
                            defaultValue: 'Tìm theo đối tượng',
                        })}
                        placeholder={t('console.audit.columnFilter.targetPlaceholder', {
                            defaultValue: 'Đối tượng…',
                        })}
                        clearLabel={clearSearchLabel}
                    />
                ),
                render: (row) => <TargetCell row={row} />,
            },
            {
                key: 'mode',
                label: modeLabel,
                width: COLUMN_WIDTHS.mode,
                control: (
                    <AuditColumnFilter
                        variant="filter"
                        active={modeActive}
                        options={showCredentialMode ? options.credentialMode : options.targetType}
                        value={showCredentialMode ? filters.credentialMode : filters.targetType}
                        onChange={handleModeChange}
                        title={modeLabel}
                        clearLabel={clearLabel}
                    />
                ),
                render: (row) => {
                    const mode = modeValueOf(row);
                    // An empty cell is an em-dash, not blank: blank reads as "the
                    // page failed to render this", the dash as "there is nothing".
                    if (!mode) return <span className="portal-audit-mode-empty">—</span>;
                    return (
                        <span className="portal-audit-mode">{modeLabels.get(mode) ?? mode}</span>
                    );
                },
            },
            {
                key: 'success',
                label: t('console.audit.columns.result', { defaultValue: 'Kết quả' }),
                width: COLUMN_WIDTHS.success,
                sortable: true,
                control: (
                    <AuditColumnFilter
                        variant="filter"
                        active={Boolean(filters.success)}
                        options={options.success}
                        value={successValue}
                        onChange={handleSuccessChange}
                        title={t('console.audit.columnFilter.result', {
                            defaultValue: 'Lọc theo kết quả',
                        })}
                        clearLabel={clearLabel}
                    />
                ),
                render: (row) => (
                    <StatusTag
                        className="portal-audit-result"
                        status={row.success ? 'success' : 'failure'}
                        config={RESULT_STATUS_CONFIG}
                    />
                ),
            },
        ] satisfies PlainTableColumn<UnifiedAuditRow>[];
    }, [
        t,
        showCredentialMode,
        filters,
        options,
        formatTimestamp,
        modeLabels,
        modeValueOf,
        successValue,
        handleDateChange,
        handleActorChange,
        handleActionChange,
        handleTargetChange,
        handleModeChange,
        handleSuccessChange,
    ]);
}
